import { useCallback, useMemo } from "react";
import { useNotification } from "../../components/Notification/NotificationProvider";
import { useTariffAccess } from "../../hooks/useTariffAccess";
import { formatTaskSchedulePeriodicity } from "../../utils/formatSchedulePeriodicity";
import type { CheckDetailKind } from "./checkDetailKind";
import { useActionsMenu } from "./hooks/useActionsMenu";
import { useRequestData } from "./hooks/useRequestData";
import { useRequestEdit } from "./hooks/useRequestEdit";
import { useRequestNavigation } from "./hooks/useRequestNavigation";
import { useRequestSchedule } from "./hooks/useRequestSchedule";
import { useRequestTaskListSidebar } from "./hooks/useRequestTaskListSidebar";
import { useRequestTasks } from "./hooks/useRequestTasks";

export const useRequestDetailPage = (
  requestId: string,
  kind: CheckDetailKind = "site"
) => {
  const { showNotification } = useNotification();
  const tariffAccess = useTariffAccess();
  const { handleBack } = useRequestNavigation();
  const actionsMenu = useActionsMenu();
  const tasks = useRequestTasks(requestId, kind);

  const request = useRequestData(requestId, {
    kind,
    showNotification,
    onLoaded: () => {
      tasks.refreshTaskListRef.current({ resetSelection: true });
    },
    refreshTaskList: () => tasks.refreshTaskListRef.current({ resetSelection: true }),
  });

  const sidebar = useRequestTaskListSidebar({
    requestId,
    kind,
    taskList: tasks.taskList,
    selectedTaskIndex: tasks.selectedTaskIndex,
    setSelectedTaskIndex: tasks.setSelectedTaskIndex,
    ensureTaskLoadedInMainList: tasks.ensureTaskLoadedInMainList,
    closeActionsMenu: actionsMenu.closeActionsMenu,
  });

  const edit = useRequestEdit({
    kind,
    data: request.data,
    setData: request.setData,
    taskList: tasks.taskList,
    taskListLoading: tasks.taskListLoading,
    showNotification,
    closePanels: () => {
      sidebar.closeAuxiliaryPanels();
      actionsMenu.closeActionsMenu();
    },
  });

  const schedule = useRequestSchedule({
    kind,
    data: request.data,
    loading: request.loading,
    setData: request.setData,
    showNotification,
    closeActionsMenu: actionsMenu.closeActionsMenu,
    canScanBySchedule: tariffAccess.canScanBySchedule,
  });

  const schedulePeriodicityLabel = useMemo(
    () =>
      formatTaskSchedulePeriodicity(
        schedule.scheduleItem ? [schedule.scheduleItem] : request.data?.schedule
      ),
    [schedule.scheduleItem, request.data?.schedule]
  );

  const canRun = kind === "it" ? tariffAccess.canItCheck : tariffAccess.canRunChecks;
  const accessBanner = kind === "it" ? tariffAccess.itCheckBanner : tariffAccess.banner;
  const accessBlockReason =
    kind === "it" ? tariffAccess.itCheckBlockReason : tariffAccess.blockReason;

  const scheduleDisabled =
    request.scheduling ||
    tasks.hasNewTask ||
    tasks.hasInProgressTask ||
    !canRun;

  const { handleDownloadPdf: downloadTaskPdf, ...tasksRest } = tasks;

  const handleDownloadPdf = useCallback(async () => {
    try {
      await downloadTaskPdf(request.data);
    } catch (err: unknown) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Не удалось скачать PDF-отчёт";
      showNotification({ message, variant: "error" });
    }
  }, [downloadTaskPdf, request.data, showNotification]);

  return {
    ...request,
    ...tasksRest,
    ...sidebar,
    ...edit,
    ...schedule,
    ...actionsMenu,
    kind,
    tariffAccess,
    accessBanner,
    accessBlockReason,
    scheduleDisabled,
    schedulePeriodicityLabel,
    handleBack,
    handleDownloadPdf,
  };
};
